import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ArrowLeft, HelpCircle, Key, Settings, Play, BarChart3, 
  Shield, AlertTriangle, CheckCircle, ChevronRight, ExternalLink,
  Wallet, TrendingUp, Zap, Grid3X3, Activity, BookOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Step component for instructions
const Step = ({ number, title, children }) => (
  <div className="flex gap-4 mb-6">
    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
      {number}
    </div>
    <div className="flex-1">
      <h4 className="text-white font-semibold mb-2">{title}</h4>
      <div className="text-blue-200 text-sm space-y-2">{children}</div>
    </div>
  </div>
);

// Warning box component
const Warning = ({ children }) => (
  <Alert className="bg-yellow-500/20 border-yellow-500/50 my-4">
    <AlertTriangle className="h-4 w-4 text-yellow-400" />
    <AlertDescription className="text-yellow-200">{children}</AlertDescription>
  </Alert>
);

// Tip box component
const Tip = ({ children }) => (
  <Alert className="bg-green-500/20 border-green-500/50 my-4">
    <CheckCircle className="h-4 w-4 text-green-400" />
    <AlertDescription className="text-green-200">{children}</AlertDescription>
  </Alert>
);

// Exchange setup guide component
const ExchangeGuide = ({ name, logo, steps, apiUrl, notes }) => (
  <Card className="bg-white/5 border-white/10">
    <CardHeader>
      <CardTitle className="text-white flex items-center gap-2">
        {logo}
        {name} API Setup
      </CardTitle>
      <CardDescription className="text-blue-200">
        <a href={apiUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400 hover:underline">
          Open {name} API Settings <ExternalLink className="h-3 w-3" />
        </a>
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      {steps.map((step, index) => (
        <Step key={index} number={index + 1} title={step.title}>
          {step.content}
        </Step>
      ))}
      {notes && <Tip>{notes}</Tip>}
    </CardContent>
  </Card>
);

const HelpPage = () => {
  const [activeTab, setActiveTab] = useState('getting-started');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard">
            <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BookOpen className="h-6 w-6" />
              Help & Setup Guide
            </h1>
            <p className="text-blue-200 text-sm">Complete guide to setting up and using the trading bot</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/10 border-white/20 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="getting-started" className="data-[state=active]:bg-blue-600 text-white">
              <Play className="mr-2 h-4 w-4" />
              Getting Started
            </TabsTrigger>
            <TabsTrigger value="api-setup" className="data-[state=active]:bg-blue-600 text-white">
              <Key className="mr-2 h-4 w-4" />
              API Setup
            </TabsTrigger>
            <TabsTrigger value="strategies" className="data-[state=active]:bg-blue-600 text-white">
              <BarChart3 className="mr-2 h-4 w-4" />
              Strategies
            </TabsTrigger>
            <TabsTrigger value="trading" className="data-[state=active]:bg-blue-600 text-white">
              <TrendingUp className="mr-2 h-4 w-4" />
              Trading
            </TabsTrigger>
            <TabsTrigger value="faq" className="data-[state=active]:bg-blue-600 text-white">
              <HelpCircle className="mr-2 h-4 w-4" />
              FAQ
            </TabsTrigger>
          </TabsList>

          {/* Getting Started Tab */}
          <TabsContent value="getting-started" className="space-y-6">
            <Card className="bg-white/10 border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Welcome to the Trading Bot</CardTitle>
                <CardDescription className="text-blue-200">
                  Follow these steps to start automated trading
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-4 gap-4 mb-8">
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold">1</span>
                    </div>
                    <p className="text-white font-medium">Create Account</p>
                    <p className="text-blue-300 text-xs">Sign up & verify email</p>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold">2</span>
                    </div>
                    <p className="text-white font-medium">Get API Keys</p>
                    <p className="text-blue-300 text-xs">From your exchange</p>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold">3</span>
                    </div>
                    <p className="text-white font-medium">Configure Bot</p>
                    <p className="text-blue-300 text-xs">Choose strategy & settings</p>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold">4</span>
                    </div>
                    <p className="text-white font-medium">Start Trading</p>
                    <p className="text-blue-300 text-xs">Monitor & profit</p>
                  </div>
                </div>

                <h3 className="text-white text-lg font-semibold border-b border-white/20 pb-2">What is an API Key?</h3>
                <p className="text-blue-200">
                  An <strong>API key</strong> is like a special password that allows our trading bot to communicate with your exchange account. 
                  Think of it as giving the bot permission to:
                </p>
                <ul className="list-disc list-inside text-blue-200 space-y-1 ml-4">
                  <li>View your account balance</li>
                  <li>See current market prices</li>
                  <li>Place buy and sell orders on your behalf</li>
                </ul>
                
                <Warning>
                  <strong>Important Security Note:</strong> Never share your API keys with anyone. 
                  We store your keys securely and encrypted. Always disable "Withdrawal" permissions 
                  when creating API keys - the bot only needs trading permissions.
                </Warning>

                <h3 className="text-white text-lg font-semibold border-b border-white/20 pb-2 mt-6">Paper Trading vs Live Trading</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <h4 className="text-green-400 font-semibold flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Paper Trading (Recommended for Beginners)
                    </h4>
                    <p className="text-blue-200 text-sm mt-2">
                      Simulated trading with fake money. Perfect for learning how the bot works 
                      without risking real funds. No API keys required!
                    </p>
                  </div>
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <h4 className="text-yellow-400 font-semibold flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Live Trading
                    </h4>
                    <p className="text-blue-200 text-sm mt-2">
                      Real trading with real money. Only use this after you understand the bot 
                      and have tested your strategy with paper trading.
                    </p>
                  </div>
                </div>

                <Tip>
                  <strong>Start with Paper Trading!</strong> Even experienced traders should test 
                  new strategies in paper trading mode first. This lets you see how the bot performs 
                  without any financial risk.
                </Tip>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Setup Tab */}
          <TabsContent value="api-setup" className="space-y-6">
            <Card className="bg-white/10 border-white/20 mb-6">
              <CardHeader>
                <CardTitle className="text-white">Exchange API Setup Guides</CardTitle>
                <CardDescription className="text-blue-200">
                  Select your exchange below for step-by-step instructions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Warning>
                  <strong>Security First!</strong> When creating API keys:
                  <ul className="list-disc list-inside mt-2">
                    <li>✅ Enable "Read" and "Trade" permissions</li>
                    <li>❌ NEVER enable "Withdraw" permission</li>
                    <li>Consider setting IP restrictions for extra security</li>
                  </ul>
                </Warning>
              </CardContent>
            </Card>

            {/* Binance */}
            <ExchangeGuide
              name="Binance"
              logo={<Badge className="bg-yellow-500">Binance</Badge>}
              apiUrl="https://www.binance.com/en/my/settings/api-management"
              steps={[
                {
                  title: "Log in to Binance",
                  content: <p>Go to binance.com and log in to your account. You may need to complete 2FA verification.</p>
                },
                {
                  title: "Navigate to API Management",
                  content: <p>Click on your profile icon → "API Management", or go directly to the API settings page.</p>
                },
                {
                  title: "Create a new API Key",
                  content: (
                    <>
                      <p>Click "Create API" and choose "System generated" for the key type.</p>
                      <p>Give it a label like "TradingBot" so you remember what it's for.</p>
                    </>
                  )
                },
                {
                  title: "Set Permissions",
                  content: (
                    <>
                      <p>Enable these permissions:</p>
                      <ul className="list-disc list-inside ml-4">
                        <li>✅ Enable Reading</li>
                        <li>✅ Enable Spot & Margin Trading</li>
                        <li>❌ Do NOT enable Withdrawals</li>
                      </ul>
                    </>
                  )
                },
                {
                  title: "Copy Your Keys",
                  content: (
                    <>
                      <p>Copy the <strong>API Key</strong> and <strong>Secret Key</strong>.</p>
                      <p className="text-yellow-300">⚠️ The Secret Key is only shown once! Save it securely.</p>
                    </>
                  )
                },
                {
                  title: "Enter Keys in Our App",
                  content: <p>Go to Account → Exchange Configuration → Select Binance → Paste your keys → Save.</p>
                }
              ]}
              notes="Binance.com is not available in the US. US users should use Binance.US instead, which has a separate API setup process."
            />

            {/* Coinbase */}
            <ExchangeGuide
              name="Coinbase"
              logo={<Badge className="bg-blue-500">Coinbase</Badge>}
              apiUrl="https://www.coinbase.com/settings/api"
              steps={[
                {
                  title: "Log in to Coinbase",
                  content: <p>Go to coinbase.com and log in. Navigate to Settings → API.</p>
                },
                {
                  title: "Create New API Key",
                  content: <p>Click "New API Key". You'll need to verify with 2FA.</p>
                },
                {
                  title: "Select Permissions",
                  content: (
                    <>
                      <p>Select these permissions:</p>
                      <ul className="list-disc list-inside ml-4">
                        <li>✅ wallet:accounts:read</li>
                        <li>✅ wallet:trades:read</li>
                        <li>✅ wallet:trades:create</li>
                        <li>❌ Do NOT select any withdrawal permissions</li>
                      </ul>
                    </>
                  )
                },
                {
                  title: "Copy Your Keys",
                  content: <p>Copy the API Key and API Secret. Enter them in our app under Exchange Configuration.</p>
                }
              ]}
            />

            {/* Kraken */}
            <ExchangeGuide
              name="Kraken"
              logo={<Badge className="bg-purple-500">Kraken</Badge>}
              apiUrl="https://www.kraken.com/u/security/api"
              steps={[
                {
                  title: "Log in to Kraken",
                  content: <p>Go to kraken.com and sign in. Navigate to Security → API.</p>
                },
                {
                  title: "Generate New Key",
                  content: <p>Click "Generate New Key". Name it something memorable like "TradingBot".</p>
                },
                {
                  title: "Configure Permissions",
                  content: (
                    <>
                      <p>Enable these permissions:</p>
                      <ul className="list-disc list-inside ml-4">
                        <li>✅ Query Funds</li>
                        <li>✅ Query Open Orders & Trades</li>
                        <li>✅ Create & Modify Orders</li>
                        <li>❌ Do NOT enable Withdraw Funds</li>
                      </ul>
                    </>
                  )
                },
                {
                  title: "Save Your Keys",
                  content: <p>Copy both the API Key and Private Key. The private key is only shown once!</p>
                }
              ]}
            />

            {/* ByBit */}
            <ExchangeGuide
              name="ByBit"
              logo={<Badge className="bg-orange-500">ByBit</Badge>}
              apiUrl="https://www.bybit.com/app/user/api-management"
              steps={[
                {
                  title: "Log in to ByBit",
                  content: <p>Go to bybit.com and log in. Click on your profile → API.</p>
                },
                {
                  title: "Create New Key",
                  content: <p>Click "Create New Key". Select "System-generated API Keys".</p>
                },
                {
                  title: "Set Permissions",
                  content: (
                    <>
                      <p>Configure these settings:</p>
                      <ul className="list-disc list-inside ml-4">
                        <li>✅ Read-Write for Spot trading</li>
                        <li>Optionally set IP restrictions</li>
                        <li>❌ No withdrawal permissions</li>
                      </ul>
                    </>
                  )
                },
                {
                  title: "Copy Keys",
                  content: <p>Save your API Key and Secret Key. Enter them in our app.</p>
                }
              ]}
            />

            {/* OKX */}
            <ExchangeGuide
              name="OKX"
              logo={<Badge className="bg-gray-500">OKX</Badge>}
              apiUrl="https://www.okx.com/account/my-api"
              steps={[
                {
                  title: "Log in to OKX",
                  content: <p>Go to okx.com and sign in. Navigate to Profile → API.</p>
                },
                {
                  title: "Create API Key",
                  content: <p>Click "Create API Key". Enter a name and your fund password.</p>
                },
                {
                  title: "Configure Permissions",
                  content: (
                    <>
                      <p>Enable:</p>
                      <ul className="list-disc list-inside ml-4">
                        <li>✅ Read</li>
                        <li>✅ Trade</li>
                        <li>❌ Withdraw - leave disabled</li>
                      </ul>
                    </>
                  )
                },
                {
                  title: "Important: Get Passphrase",
                  content: (
                    <>
                      <p>OKX requires a <strong>Passphrase</strong> in addition to API Key and Secret.</p>
                      <p>You create this passphrase during API creation. Remember it!</p>
                    </>
                  )
                },
                {
                  title: "Enter All Three in Our App",
                  content: <p>You'll need: API Key, Secret Key, AND Passphrase.</p>
                }
              ]}
              notes="OKX requires a passphrase - make sure to save it when creating the API key!"
            />

            {/* Gate.io */}
            <ExchangeGuide
              name="Gate.io"
              logo={<Badge className="bg-teal-500">Gate.io</Badge>}
              apiUrl="https://www.gate.io/myaccount/api_key_manage"
              steps={[
                {
                  title: "Log in to Gate.io",
                  content: <p>Go to gate.io and log in. Navigate to API Management in your account settings.</p>
                },
                {
                  title: "Create API Key",
                  content: <p>Click "Create API Key". Complete the security verification.</p>
                },
                {
                  title: "Set Permissions",
                  content: (
                    <>
                      <p>Enable:</p>
                      <ul className="list-disc list-inside ml-4">
                        <li>✅ Spot Trade</li>
                        <li>✅ Read permission</li>
                        <li>❌ Withdraw - keep disabled</li>
                      </ul>
                    </>
                  )
                },
                {
                  title: "Copy Your Keys",
                  content: <p>Save both the Key and Secret. Enter them in our Exchange Configuration.</p>
                }
              ]}
            />

            {/* Bitfinex */}
            <ExchangeGuide
              name="Bitfinex"
              logo={<Badge className="bg-green-600">Bitfinex</Badge>}
              apiUrl="https://www.bitfinex.com/api"
              steps={[
                {
                  title: "Log in to Bitfinex",
                  content: <p>Go to bitfinex.com and log in. Navigate to Account → API Keys.</p>
                },
                {
                  title: "Create New Key",
                  content: <p>Click "Create New Key". Label it for identification.</p>
                },
                {
                  title: "Set Permissions",
                  content: (
                    <>
                      <p>Enable these permissions:</p>
                      <ul className="list-disc list-inside ml-4">
                        <li>✅ Read</li>
                        <li>✅ Write (for placing orders)</li>
                        <li>❌ Withdraw - keep disabled</li>
                      </ul>
                    </>
                  )
                },
                {
                  title: "Copy API Key and Secret",
                  content: <p>Copy both values and enter them in our app's Exchange Configuration.</p>
                }
              ]}
            />
          </TabsContent>

          {/* Strategies Tab */}
          <TabsContent value="strategies" className="space-y-6">
            <Card className="bg-white/10 border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Trading Strategies Explained</CardTitle>
                <CardDescription className="text-blue-200">
                  Understanding when each strategy works best
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-blue-200">
                  The bot offers four different trading strategies. Each has its strengths depending on market conditions.
                  Here's a beginner-friendly explanation of each:
                </p>

                {/* Mean Reversion RSI */}
                <div className="p-6 bg-white/5 rounded-lg border border-blue-500/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Activity className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">Mean Reversion (RSI)</h3>
                      <Badge className="bg-green-500">Beginner Friendly</Badge>
                    </div>
                  </div>
                  <p className="text-blue-200 mb-4">
                    <strong>The Idea:</strong> Prices tend to return to their average over time. When something is 
                    "oversold" (too cheap), it often bounces back up. When it's "overbought" (too expensive), it often drops.
                  </p>
                  <p className="text-blue-200 mb-4">
                    <strong>How it Works:</strong> Uses the RSI (Relative Strength Index) indicator, which measures 
                    how "oversold" or "overbought" an asset is on a scale of 0-100.
                  </p>
                  <ul className="text-blue-200 space-y-2 mb-4">
                    <li>📈 <strong>RSI below 30:</strong> Asset is oversold → Bot considers BUYING</li>
                    <li>📉 <strong>RSI above 70:</strong> Asset is overbought → Bot considers SELLING</li>
                  </ul>
                  <p className="text-green-300">
                    <strong>Best For:</strong> Sideways/ranging markets where prices bounce between highs and lows.
                  </p>
                  <p className="text-red-300">
                    <strong>Avoid When:</strong> Markets are trending strongly in one direction.
                  </p>
                </div>

                {/* Breakout Strategy */}
                <div className="p-6 bg-white/5 rounded-lg border border-purple-500/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                      <Zap className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">Breakout Strategy</h3>
                      <Badge className="bg-yellow-500">Intermediate</Badge>
                    </div>
                  </div>
                  <p className="text-blue-200 mb-4">
                    <strong>The Idea:</strong> When prices break out of a trading range, they often continue moving 
                    in that direction with momentum.
                  </p>
                  <p className="text-blue-200 mb-4">
                    <strong>How it Works:</strong> Uses Donchian Channels (the highest high and lowest low over a period) 
                    to identify when price breaks through these levels.
                  </p>
                  <ul className="text-blue-200 space-y-2 mb-4">
                    <li>🚀 <strong>Price breaks above channel:</strong> Potential upward breakout → Bot BUYS</li>
                    <li>💥 <strong>Price breaks below channel:</strong> Potential breakdown → Bot may SHORT</li>
                    <li>⚡ <strong>Squeeze detection:</strong> Low volatility often precedes big moves</li>
                  </ul>
                  <p className="text-green-300">
                    <strong>Best For:</strong> Volatile markets, news events, and trend beginnings.
                  </p>
                  <p className="text-red-300">
                    <strong>Avoid When:</strong> Markets are choppy with many false breakouts.
                  </p>
                </div>

                {/* Trend Following */}
                <div className="p-6 bg-white/5 rounded-lg border border-green-500/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">Trend Following</h3>
                      <Badge className="bg-blue-500">Intermediate</Badge>
                    </div>
                  </div>
                  <p className="text-blue-200 mb-4">
                    <strong>The Idea:</strong> "The trend is your friend." When markets are trending, it's often 
                    profitable to follow that direction rather than fight it.
                  </p>
                  <p className="text-blue-200 mb-4">
                    <strong>How it Works:</strong> Uses two Moving Averages (fast and slow) plus ADX (trend strength indicator).
                  </p>
                  <ul className="text-blue-200 space-y-2 mb-4">
                    <li>📈 <strong>Fast MA crosses above Slow MA:</strong> Uptrend signal → Bot BUYS</li>
                    <li>📉 <strong>Fast MA crosses below Slow MA:</strong> Downtrend signal → Bot SELLS</li>
                    <li>💪 <strong>ADX confirms strength:</strong> Only trades when trend is strong enough</li>
                  </ul>
                  <p className="text-green-300">
                    <strong>Best For:</strong> Clear trending markets (bull runs or bear markets).
                  </p>
                  <p className="text-red-300">
                    <strong>Avoid When:</strong> Markets are moving sideways with no clear direction.
                  </p>
                </div>

                {/* Grid Trading */}
                <div className="p-6 bg-white/5 rounded-lg border border-cyan-500/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center">
                      <Grid3X3 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">Grid Trading</h3>
                      <Badge className="bg-orange-500">Advanced</Badge>
                    </div>
                  </div>
                  <p className="text-blue-200 mb-4">
                    <strong>The Idea:</strong> Place multiple buy orders below current price and sell orders above. 
                    Profit from price oscillations within a range.
                  </p>
                  <p className="text-blue-200 mb-4">
                    <strong>How it Works:</strong> Creates a "grid" of price levels. Buys when price drops to a level, 
                    sells when it rises to the next level.
                  </p>
                  <ul className="text-blue-200 space-y-2 mb-4">
                    <li>🔽 <strong>Price drops to grid level:</strong> Bot places BUY order</li>
                    <li>🔼 <strong>Price rises to next level:</strong> Bot places SELL order</li>
                    <li>🔄 <strong>Cycle repeats:</strong> Profits from each up/down movement</li>
                  </ul>
                  <p className="text-green-300">
                    <strong>Best For:</strong> Sideways/ranging markets with consistent oscillations.
                  </p>
                  <p className="text-red-300">
                    <strong>Avoid When:</strong> Strong trending markets (can accumulate losses on wrong side).
                  </p>
                  <Warning>
                    Grid trading requires more capital and careful configuration. Start with paper trading!
                  </Warning>
                </div>

                <Tip>
                  <strong>Pro Tip:</strong> No strategy works in all market conditions. The best approach is to 
                  understand current market conditions and choose the appropriate strategy. When in doubt, 
                  start with Mean Reversion RSI as it's the most beginner-friendly.
                </Tip>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trading Tab */}
          <TabsContent value="trading" className="space-y-6">
            <Card className="bg-white/10 border-white/20">
              <CardHeader>
                <CardTitle className="text-white">How to Start Trading</CardTitle>
                <CardDescription className="text-blue-200">
                  Step-by-step guide to running your first trade
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Step number={1} title="Go to Account Page">
                  <p>Click on "Account" in the navigation menu to access your settings.</p>
                </Step>

                <Step number={2} title="Configure Your Exchange">
                  <p>In the "Exchange Configuration" tab:</p>
                  <ul className="list-disc list-inside ml-4 mt-2">
                    <li>Select your exchange from the dropdown</li>
                    <li>Enter your API Key and Secret</li>
                    <li>For OKX, also enter the Passphrase</li>
                    <li>Click "Save Configuration"</li>
                  </ul>
                </Step>

                <Step number={3} title="Choose Your Trading Mode">
                  <p>In the "Bot Settings" tab, select either:</p>
                  <ul className="list-disc list-inside ml-4 mt-2">
                    <li><strong>Paper Trading:</strong> Practice mode with simulated money (recommended for beginners)</li>
                    <li><strong>Live Trading:</strong> Real money trading (only after testing!)</li>
                  </ul>
                </Step>

                <Step number={4} title="Select a Strategy">
                  <p>Choose a trading strategy that matches current market conditions. See the Strategies tab for details on each one.</p>
                </Step>

                <Step number={5} title="Configure Risk Settings">
                  <p>Set your risk parameters:</p>
                  <ul className="list-disc list-inside ml-4 mt-2">
                    <li><strong>Trade Amount:</strong> How much to trade per position</li>
                    <li><strong>Stop Loss %:</strong> Maximum loss before automatic exit</li>
                    <li><strong>Take Profit %:</strong> Target profit for automatic exit</li>
                  </ul>
                </Step>

                <Step number={6} title="Save and Go to Dashboard">
                  <p>Click "Save Settings" and navigate to the Dashboard.</p>
                </Step>

                <Step number={7} title="Start the Bot">
                  <p>Click the green "Start Bot" button. The bot will:</p>
                  <ul className="list-disc list-inside ml-4 mt-2">
                    <li>Connect to your exchange</li>
                    <li>Start monitoring prices</li>
                    <li>Execute trades based on your strategy</li>
                  </ul>
                </Step>

                <Warning>
                  <strong>Always start with Paper Trading!</strong> Test your strategy and settings before risking real money. 
                  Even experienced traders use paper trading to test new strategies.
                </Warning>

                <h3 className="text-white text-lg font-semibold border-b border-white/20 pb-2 mt-8">Understanding the Dashboard</h3>
                
                <div className="space-y-4 mt-4">
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium">Bot Status</h4>
                    <p className="text-blue-200 text-sm">Shows if the bot is running, paused, or stopped. Green = running.</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium">Current Price</h4>
                    <p className="text-blue-200 text-sm">Real-time price of your trading pair with bid/ask spread.</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium">Strategy Indicators</h4>
                    <p className="text-blue-200 text-sm">Visual display of your strategy's signals (RSI, EMAs, etc.).</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium">Current Position</h4>
                    <p className="text-blue-200 text-sm">If you have an open trade, shows entry price, current P&L, and position size.</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium">Manual Controls</h4>
                    <p className="text-blue-200 text-sm">Buttons to manually buy, sell, or close positions. Use to override the bot if needed.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq" className="space-y-6">
            <Card className="bg-white/10 border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium mb-2">Is my money safe?</h4>
                    <p className="text-blue-200 text-sm">
                      Your funds stay on your exchange - we never have access to withdraw them. 
                      API keys are encrypted and stored securely. Always disable withdrawal permissions on your API keys.
                    </p>
                  </div>

                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium mb-2">How much can I make?</h4>
                    <p className="text-blue-200 text-sm">
                      Returns vary based on market conditions, strategy selection, and settings. 
                      Past performance doesn't guarantee future results. Start with paper trading to understand realistic expectations.
                    </p>
                  </div>

                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium mb-2">What happens if the bot loses money?</h4>
                    <p className="text-blue-200 text-sm">
                      Trading involves risk. Use stop-loss settings to limit potential losses. 
                      Never trade more than you can afford to lose. The bot executes your strategy - market conditions determine profitability.
                    </p>
                  </div>

                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium mb-2">Does the bot trade 24/7?</h4>
                    <p className="text-blue-200 text-sm">
                      Yes, once started, the bot monitors markets and trades around the clock. 
                      Crypto markets never close, so the bot works even while you sleep.
                    </p>
                  </div>

                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium mb-2">What are credits used for?</h4>
                    <p className="text-blue-200 text-sm">
                      Credits are used to pay for trading fees on our platform (small commission per trade). 
                      Paper trading doesn't use credits. You receive free credits when signing up.
                    </p>
                  </div>

                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium mb-2">Can I use multiple exchanges?</h4>
                    <p className="text-blue-200 text-sm">
                      Currently, you can configure one exchange at a time. Switch between exchanges 
                      in your Account settings whenever needed.
                    </p>
                  </div>

                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium mb-2">Why is my exchange showing "Connection Failed"?</h4>
                    <p className="text-blue-200 text-sm">
                      Check that: (1) Your API keys are correct, (2) API permissions include trading, 
                      (3) Your exchange account is verified, (4) No IP restrictions are blocking the connection.
                    </p>
                  </div>

                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white font-medium mb-2">How do I stop the bot?</h4>
                    <p className="text-blue-200 text-sm">
                      Click the "Stop" button on the Dashboard. This will stop new trades but won't 
                      automatically close open positions. Use "Close Position" to exit any open trades.
                    </p>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                  <h4 className="text-white font-medium mb-2">Still have questions?</h4>
                  <p className="text-blue-200 text-sm">
                    Contact support or check our community forums for more help. 
                    We're here to help you succeed with automated trading!
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default HelpPage;
